/**
 * Application Constants
 */

// API Base URL
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'admin_auth_token',
  USER: 'admin_user'
};

// Game Status Values
// Note: wip covers both new games (releaseChannel=null) and modifications (releaseChannel=beta/curated/community)
export const GAME_STATUS = {
  WIP: 'wip',
  PENDING_REVIEW: 'pending_review',
  READY_FOR_PROD: 'ready_for_prod',
  IN_PRODUCTION: 'in_production',
  FLAGGED_DELETE: 'flagged_delete'
};

// Game Status Labels (for display)
export const STATUS_LABELS = {
  [GAME_STATUS.WIP]: 'Work in Progress',
  [GAME_STATUS.PENDING_REVIEW]: 'Pending Review',
  [GAME_STATUS.READY_FOR_PROD]: 'Ready for Production',
  [GAME_STATUS.IN_PRODUCTION]: 'In Production',
  [GAME_STATUS.FLAGGED_DELETE]: 'Flagged for Deletion'
};

// Status Badge Colors (Material-UI colors)
export const STATUS_COLORS = {
  [GAME_STATUS.WIP]: 'default',
  [GAME_STATUS.PENDING_REVIEW]: 'warning',
  [GAME_STATUS.READY_FOR_PROD]: 'success',
  [GAME_STATUS.IN_PRODUCTION]: 'primary',
  [GAME_STATUS.FLAGGED_DELETE]: 'error'
};

// Scoring Types (camelCase per ValidationRules.md)
export const SCORING_TYPES = {
  WIN_LOSS: 'winLoss',
  SCORE: 'score',
  MULTI_ROUNDS: 'multiRounds',
  MULTI_ROUNDS_PLUS: 'multiRounds+',
  STREAM_EVENTS: 'streamEvents'  // Space Base-style: free-flowing event stream, no rounds
};

export const SCORING_TYPE_LABELS = {
  [SCORING_TYPES.WIN_LOSS]: 'Win/Loss',
  [SCORING_TYPES.SCORE]: 'Score',
  [SCORING_TYPES.MULTI_ROUNDS]: 'Multi-Round',
  [SCORING_TYPES.MULTI_ROUNDS_PLUS]: 'Multi-Round + Ending',
  [SCORING_TYPES.STREAM_EVENTS]: 'Stream Events'
};

export const SCORING_TYPE_DESCRIPTIONS = {
  [SCORING_TYPES.WIN_LOSS]: 'Simple winner selection with no numeric scores',
  [SCORING_TYPES.SCORE]: 'Single scoring event at end of game',
  [SCORING_TYPES.MULTI_ROUNDS]: 'Multiple scoring rounds',
  [SCORING_TYPES.MULTI_ROUNDS_PLUS]: 'Multiple rounds with phase-end scoring',
  [SCORING_TYPES.STREAM_EVENTS]: 'Free-flowing events with no round structure (e.g., Space Base)'
};

// Release Channels
// null = not in release flow (new wip games)
// beta = beta testing
// community = promoted from beta
// curated = officially approved
// deprecated = auto-demoted due to poor ratings
export const RELEASE_CHANNELS = {
  CURATED: 'curated',
  COMMUNITY: 'community',
  BETA: 'beta',
  DEPRECATED: 'deprecated'
};

export const RELEASE_CHANNEL_LABELS = {
  [RELEASE_CHANNELS.CURATED]: 'Curated',
  [RELEASE_CHANNELS.COMMUNITY]: 'Community',
  [RELEASE_CHANNELS.BETA]: 'Beta',
  [RELEASE_CHANNELS.DEPRECATED]: 'Deprecated'
};

export const RELEASE_CHANNEL_COLORS = {
  [RELEASE_CHANNELS.CURATED]: 'primary',
  [RELEASE_CHANNELS.COMMUNITY]: 'secondary',
  [RELEASE_CHANNELS.BETA]: 'warning',
  [RELEASE_CHANNELS.DEPRECATED]: 'error'
};

// Phase End Types
export const PHASE_END_TYPES = {
  REPEATING: 'repeating',
  END_GAME: 'endGame'
};

export const PHASE_END_TYPE_LABELS = {
  [PHASE_END_TYPES.REPEATING]: 'Repeating',
  [PHASE_END_TYPES.END_GAME]: 'End Game'
};

// Phase Triggers
export const PHASE_TRIGGERS = {
  MANUAL: 'manual',
  AUTOMATIC: 'automatic'
};

export const PHASE_TRIGGER_LABELS = {
  [PHASE_TRIGGERS.MANUAL]: 'Manual',
  [PHASE_TRIGGERS.AUTOMATIC]: 'Automatic'
};

// Round Behavior Options
export const ROUND_BEHAVIORS = {
  UP: 'up',
  DOWN: 'down',
  UP_DOWN: 'updown'
};

export const ROUND_BEHAVIOR_LABELS = {
  [ROUND_BEHAVIORS.UP]: 'Up (increasing)',
  [ROUND_BEHAVIORS.DOWN]: 'Down (decreasing)',
  [ROUND_BEHAVIORS.UP_DOWN]: 'Up-Down'
};

// Win Conditions
export const WIN_CONDITIONS = ['highScore', 'lowScore', 'firstToGoal', 'other'];

// Entry Styles (for score entry UI)
export const ENTRY_STYLES = {
  INLINE: 'inline',
  COMPACT: 'compact',
  POPUP: 'popup'
};

export const ENTRY_STYLE_LABELS = {
  [ENTRY_STYLES.INLINE]: 'Inline',
  [ENTRY_STYLES.COMPACT]: 'Compact',
  [ENTRY_STYLES.POPUP]: 'Popup'
};

export const ENTRY_STYLE_DESCRIPTIONS = {
  [ENTRY_STYLES.INLINE]: 'One input cell per category in a grid layout',
  [ENTRY_STYLES.COMPACT]: 'Single score value per player per round',
  [ENTRY_STYLES.POPUP]: 'Tap player to open score entry form (best for 4+ categories)'
};

// Languages
export const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' }
];

// Schema Versions
export const SCHEMA_VERSIONS = [1]; // Expand as new schema versions are added

// Source Types
export const SOURCE_TYPES = {
  NEW_GAME: 'new_game',
  IMPORTED: 'imported',
  MODIFIED_SYSTEM: 'modified_system',
  TRANSLATED: 'translated',
  VARIANT: 'variant',
  SUBMITTED_GAME: 'submitted_game'
};

// Submission Status Values
export const SUBMISSION_STATUS = {
  PENDING: 'pending',
  IN_REVIEW: 'in_review',
  IN_BETA: 'in_beta',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  NEEDS_INFO: 'needs_info',
  RESPONSE_RECEIVED: 'response_received',
  CANCELLED: 'cancelled'
};

// Submission Status Labels
export const SUBMISSION_STATUS_LABELS = {
  [SUBMISSION_STATUS.PENDING]: 'Pending',
  [SUBMISSION_STATUS.IN_REVIEW]: 'In Review',
  [SUBMISSION_STATUS.IN_BETA]: 'In Beta',
  [SUBMISSION_STATUS.ACCEPTED]: 'Accepted',
  [SUBMISSION_STATUS.REJECTED]: 'Rejected',
  [SUBMISSION_STATUS.NEEDS_INFO]: 'Needs Info',
  [SUBMISSION_STATUS.RESPONSE_RECEIVED]: 'Response Received',
  [SUBMISSION_STATUS.CANCELLED]: 'Cancelled'
};

// Submission Status Colors
export const SUBMISSION_STATUS_COLORS = {
  [SUBMISSION_STATUS.PENDING]: 'warning',
  [SUBMISSION_STATUS.IN_REVIEW]: 'info',
  [SUBMISSION_STATUS.IN_BETA]: 'secondary',
  [SUBMISSION_STATUS.ACCEPTED]: 'success',
  [SUBMISSION_STATUS.REJECTED]: 'error',
  [SUBMISSION_STATUS.NEEDS_INFO]: 'warning',
  [SUBMISSION_STATUS.RESPONSE_RECEIVED]: 'info',
  [SUBMISSION_STATUS.CANCELLED]: 'default'
};

// API Endpoints
export const ENDPOINTS = {
  // Auth
  LOGIN: '/admin/auth/login',
  GENERATE_HASH: '/admin/auth/generate-hash',

  // Games
  GAMES: '/admin/games',
  GAME_BY_ID: (id) => `/admin/games/${id}`,
  GAME_TRANSITION: (id) => `/admin/games/${id}/transition`,
  GAME_CANCEL_MODIFICATIONS: (id) => `/admin/games/${id}/cancel-modifications`,
  GAME_FEEDBACK: (id) => `/admin/games/${id}/feedback`,

  // Submissions
  SUBMISSIONS: '/admin/submissions',
  SUBMISSION_BY_ID: (id) => `/admin/submissions/${id}`,
  SUBMISSION_ACCEPT: (id) => `/admin/submissions/${id}/accept`,
  SUBMISSION_REJECT: (id) => `/admin/submissions/${id}/reject`,
  SUBMISSION_REQUEST_INFO: (id) => `/admin/submissions/${id}/request-info`,
  SUBMISSION_STATUS: (id) => `/admin/submissions/${id}/status`
};

// Pagination
export const DEFAULT_PAGE_SIZE = 50;
export const PAGE_SIZE_OPTIONS = [25, 50, 100];

// Table Column Configuration
export const GAME_TABLE_COLUMNS = [
  { id: 'gameCode', label: 'Game Code', width: 150 },
  { id: 'gameName', label: 'Name', width: 200 },
  { id: 'language', label: 'Lang', width: 60 },
  { id: 'schemaVersion', label: 'Schema', width: 80 },
  { id: 'category', label: 'Category', width: 120 },
  { id: 'releaseChannel', label: 'Channel', width: 100 },
  { id: 'status', label: 'Status', width: 150 },
  { id: 'contributorHandle', label: 'Contributor', width: 120 },
  { id: 'modifiedAt', label: 'Modified', width: 150 },
  { id: 'actions', label: 'Actions', width: 150 }
];
